
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Shield, User, Mail, Lock, Phone, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SuperAdminCreator = () => {
  const [secretCode, setSecretCode] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    bio: "",
    dateOfBirth: "",
    gender: ""
  });
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Secret code to reveal the admin creator (you can change this)
  const SECRET_CODE = "doughflow-admin-2025";

  const handleSecretSubmit = () => {
    if (secretCode === SECRET_CODE) {
      setIsVisible(true);
      toast.success("Admin creator unlocked");
    } else {
      toast.error("Invalid secret code");
    }
  };

  const normalizeGhanaPhone = (phone: string): string => {
    if (!phone) return '';
    
    const cleaned = phone.replace(/[^\d+]/g, '');
    const digitsOnly = cleaned.replace(/\+/g, '');
    
    if (digitsOnly.length === 0) return '';
    
    if (digitsOnly.startsWith('233')) {
      if (digitsOnly.length === 12) {
        return `+${digitsOnly}`;
      }
      return '';
    }
    
    if (digitsOnly.startsWith('0')) {
      if (digitsOnly.length === 10) {
        return `+233${digitsOnly.substring(1)}`;
      }
      return '';
    }
    
    if (digitsOnly.length === 9) {
      return `+233${digitsOnly}`;
    }
    
    return '';
  };

  const createSuperAdmin = async () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      toast.error("Please fill in all required fields (email, password, first name, last name)");
      return;
    }

    // Validate phone if provided
    let formattedPhone = '';
    if (formData.phone) {
      formattedPhone = normalizeGhanaPhone(formData.phone);
      if (!formattedPhone) {
        toast.error("Invalid phone number format. Please use Ghana format (e.g., 0241234567)");
        return;
      }
    }

    setLoading(true);
    try {
      // Always use create-first-admin function for admin creation
      // It will handle both first admin and additional admins
      const { data, error } = await supabase.functions.invoke('create-first-admin', {
        body: {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formattedPhone || null,
          bio: formData.bio || null,
          dateOfBirth: formData.dateOfBirth || null,
          gender: formData.gender || null
        }
      });

      if (error) {
        throw error;
      }
      
      toast.success("Admin user created successfully! You can now sign in with your credentials.");
      
      // Clear form
      setFormData({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        phone: "",
        bio: "",
        dateOfBirth: "",
        gender: ""
      });
    } catch (error: unknown) {
      console.error("Admin creation error:", error);
      toast.error(`Error creating admin: ${error instanceof Error ? error.message : "An unknown error occurred"}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Admin Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="password"
            placeholder="Enter secret code"
            value={secretCode}
            onChange={(e) => setSecretCode(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSecretSubmit()}
          />
          <Button onClick={handleSecretSubmit} className="w-full">
            Unlock Admin Creator
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Create Super Admin
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            Basic Information
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="admin@doughmanfoods.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Password *
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="Strong password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Contact Information
          </h3>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="0241234567 or +233241234567"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Ghana format: 0241234567 or +233241234567
            </p>
          </div>

          <div>
            <Label htmlFor="bio">Bio (Optional)</Label>
            <Textarea
              id="bio"
              placeholder="Brief description about the admin..."
              value={formData.bio}
              onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        {/* Personal Information */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Personal Information
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dateOfBirth">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="gender">Gender</Label>
              <Select 
                value={formData.gender} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2 border-t pt-4">
          <Button 
            onClick={createSuperAdmin} 
            disabled={loading || !formData.email || !formData.password || !formData.firstName || !formData.lastName} 
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            {loading ? "Creating Admin..." : "Create Super Admin"}
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => setIsVisible(false)} 
            className="w-full"
          >
            Hide Form
          </Button>
        </div>

        <div className="text-sm text-muted-foreground bg-amber-50 p-3 rounded-lg border border-amber-200">
          <p><strong>Note:</strong> Fields marked with * are required. This form will automatically detect if this is the first admin user and use the appropriate creation method.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperAdminCreator;
